import { apiClient } from "./client"
import type {
  Balance,
  TopUpTonRequest,
  TopUpTonResponse,
  TopUpCryptoBotRequest,
  TopUpCryptoBotResponse,
  TopUpXRocketRequest,
  TopUpXRocketResponse,
  UserAssetBalance,
  PaymentHistoryResponse,
  ReferralLinkResponse,
  UserGiftsParams,
  UserGiftsResponse,
  PlatformGiftsParams,
  PlatformGiftsResponse,
  PurchasePlatformGiftRequest,
  PurchasePlatformGiftResponse,
} from "./types"
import type { RoundSnapshot } from "./types/pvpRoulette"
import { mapRoundDtoToSnapshot } from "./round"

// Economy
export const getBalance = async () => {
  const raw = await apiClient.get<UserAssetBalance>("/v1/user/balance", {
    asset_class: "TON",
  })

  const toTonString = (value?: number) =>
    typeof value === "number" ? (value / 1e9).toString() : "0"

  const balance: Balance = {
    balance: {
      amount: toTonString(raw.available ?? raw.total),
    },
  }

  return balance
}

export const topUpTon = (data: TopUpTonRequest) =>
  apiClient.post<TopUpTonResponse>("/v1/payments/topup-ton", data)

export const topUpTonCryptoBot = (data: TopUpCryptoBotRequest) =>
  apiClient.post<TopUpCryptoBotResponse>(
    "/v1/payments/crypto-bot/topup-ton",
    data
  )

export const topUpTonXRocket = (data: TopUpXRocketRequest) =>
  apiClient.post<TopUpXRocketResponse>("/v1/payments/xrocket/topup-ton", data)

export const getPaymentHistory = (params: { limit: number; cursor?: string }) =>
  apiClient.get<PaymentHistoryResponse>("/v1/payments/history", {
    limit: params.limit,
    ...(params.cursor ? { cursor: params.cursor } : {}),
  })

export const getReferralLink = () =>
  apiClient.get<ReferralLinkResponse>("/v1/users/referral-link")

// ── Marketplace (platform gifts) ─────────────────────────────────────────────

export const getPlatformGifts = (
  params: PlatformGiftsParams = {}
): Promise<PlatformGiftsResponse> => {
  const query: Record<string, number> = {}
  if (typeof params.limit === "number") query.limit = params.limit
  return apiClient.get<PlatformGiftsResponse>("/v1/custody/platform-gifts", query)
}

export const purchasePlatformGift = (
  externalGiftId: string,
  data: PurchasePlatformGiftRequest
): Promise<PurchasePlatformGiftResponse> =>
  apiClient.post<PurchasePlatformGiftResponse>(
    `/v1/custody/platform-gifts/${encodeURIComponent(externalGiftId)}/purchase`,
    data
  )

export const getUserGifts = async (
  params: UserGiftsParams = {}
): Promise<UserGiftsResponse> => {
  const query: Record<string, string | number> = {}
  if (params.status) query.status = params.status
  if (typeof params.available_for_bet === "boolean")
    query.available_for_bet = String(params.available_for_bet)
  if (typeof params.limit === "number") query.limit = params.limit
  if (params.cursor) query.cursor = params.cursor
  const response = await apiClient.get<UserGiftsResponse>(
    "/v1/user/gifts",
    query
  )
  return response
}

export interface BootstrapResponse {
  segment_popup: unknown | null
  current_round: RoundSnapshot | null
  /** Absent when the backend omits balance on bootstrap — callers must tolerate null. */
  current_balance: UserAssetBalance | null
  /**
   * Raw `tournaments.items[]` from the backend (each item is a
   * `TournamentSummaryDTO` — UPCOMING / ACTIVE only). Parsed defensively in
   * `PvpRoomRoot` — **currently only `items[0]` is consumed** (multi-tournament
   * UI is TBD).
   */
  tournaments_items: unknown[]
}

export const getBootstrap = async (roomName: string): Promise<BootstrapResponse> => {
  const raw = await apiClient.get<{
    segment_popup?: unknown | null
    current_round?: unknown
    current_balance?: UserAssetBalance
    tournaments?: { items?: unknown[] } | null
  }>("/v1/bootstrap", {
    room_name: roomName,
  })

  let currentRound: RoundSnapshot | null = null
  if (raw.current_round && typeof raw.current_round === "object") {
    currentRound = mapRoundDtoToSnapshot(
      raw.current_round as Record<string, unknown>
    )
  }

  return {
    segment_popup: raw.segment_popup ?? null,
    current_round: currentRound,
    current_balance:
      raw.current_balance &&
      typeof raw.current_balance.total === "number" &&
      typeof raw.current_balance.available === "number" &&
      typeof raw.current_balance.held === "number"
        ? raw.current_balance
        : null,
    tournaments_items: Array.isArray(raw.tournaments?.items)
      ? raw.tournaments.items
      : [],
  }
}

/**
 * `GET /v1/games/tournaments/{id}` — tournament details (TournamentDTO with
 * reward_promises, paginated leaderboard, my_standing). Called when the user
 * opens the leaderboard page for a specific tournament. We always request
 * `limit=10` and `offset=0` — the UI doesn't paginate.
 */
export const getTournamentDetails = (
  tournamentId: string,
  params?: { limit?: number; offset?: number }
) =>
  apiClient.get<unknown>(`/v1/games/tournaments/${tournamentId}`, {
    limit: params?.limit ?? 10,
    offset: params?.offset ?? 0,
  })

// ── Withdrawal ───────────────────────────────────────────────────────────────

export interface TonWithdrawalLimits {
  /** Daily withdrawal cap, nanotons */
  max_per_day: number
  /** Minimum single withdrawal, nanotons */
  min_once: number
  /** Remaining daily allowance, nanotons */
  remaining_day: number
  balance: {
    total: number
    available: number
    held: number
  }
}

export interface WithdrawalRequest {
  asset_class: "TON" | "TELEGRAM_GIFT"
  recipient_key: string
  amount?: number | null
  nft_id?: string | null
}

export interface WithdrawalResponse {
  withdrawal_id: string
  status: "CREATED"
  created_at: number
}

export const getTonWithdrawalLimits = () =>
  apiClient.get<TonWithdrawalLimits>("/v1/payments/ton-withdrawal-limits")

export const initiateWithdrawal = (data: WithdrawalRequest) =>
  apiClient.post<WithdrawalResponse>("/v1/payments/withdrawal", data)

// PvP Roulette
export interface PlaceBetRequest {
  round_id: string
  party_id: string
  party_type: "SOLO" | "TEAM"
  asset_class: "TON" | "TELEGRAM_GIFT"
  amount?: number | null
  nft_id?: string | null
}

export interface PlaceBetResponse {
  bet_id: string
}

export const placeBet = (data: PlaceBetRequest) =>
  apiClient.request<PlaceBetResponse>("/v1/games/pvp-roulette/bet", {
    method: "POST",
    body: data,
    priority: "high",
  })

export type PvpRouletteTrickType = "PUSH_WHEEL" | "FREEZE_WHEEL"

export interface ApplyTrickRequest {
  round_id: string
  trick_type: PvpRouletteTrickType
}

export const applyTrick = (data: ApplyTrickRequest) =>
  apiClient.request("/v1/games/pvp-roulette/trick", {
    method: "POST",
    body: data,
    priority: "high",
  })

export const cancelBet = (betId: string) =>
  apiClient.request<unknown>(
    `/v1/games/pvp-roulette/bet/${encodeURIComponent(betId)}`,
    {
      method: "DELETE",
      body: {
        bet_id: betId,
      },
      priority: "high",
    }
  )

export interface PvpRouletteRoundHistoryWinnerPlayer {
  user_id: string
  username?: string | null
  photo_url?: string | null
  gross_win_amount: number
  gross_payout_amount: number
  net_win_amount: number
  net_payout_amount: number
}

export interface PvpRouletteRoundHistoryWinnerParty {
  id: string
  type: "SOLO" | "TEAM"
  name: string
  gross_win_amount: number
  gross_payout_amount: number
  net_win_amount: number
  net_payout_amount: number
  win_chance: number
  winners: PvpRouletteRoundHistoryWinnerPlayer[]
}

export interface PvpRouletteRoundHistoryItem {
  round_id: string
  number: number
  room_name: string
  commit_hash: string
  client_commit_hash: string
  salt: string
  total_bank: number
  winner_party: PvpRouletteRoundHistoryWinnerParty
  /** Unix timestamp, milliseconds (int). */
  created_at?: number | null
}

export interface PvpRouletteRoundHistoryResponse {
  items: PvpRouletteRoundHistoryItem[]
  next_cursor: string | null
}

export interface PvpRouletteRoundHistoryParams {
  room_name?: string
  only_my_rounds?: boolean
  limit: number
  cursor?: string
}

export const getPvpRouletteRoundHistory = (
  params: PvpRouletteRoundHistoryParams
) =>
  apiClient.get<PvpRouletteRoundHistoryResponse>(
    "/v1/games/pvp-roulette/round-history",
    {
      room_name: params.room_name,
      only_my_rounds: params.only_my_rounds ? 1 : 0,
      limit: params.limit,
      cursor: params.cursor,
    }
  )

// ── Round detail (resolved) ──────────────────────────────────────────────

export interface RoundDetailUserData {
  user_id: string
  username?: string | null
  photo_url?: string | null
}

export interface ActualPlayerBank {
  user_data: RoundDetailUserData
  bank: number
  gross_win_amount: number
  gross_payout_amount: number
  net_win_amount: number
  net_payout_amount: number
}

export interface ActualParty {
  id: string
  type: "SOLO" | "TEAM"
  total_bank: number
  win_probability: number
  gross_win_amount: number
  gross_payout_amount: number
  net_win_amount: number
  net_payout_amount: number
  player_banks: ActualPlayerBank[]
  sector_from: number
  sector_to: number
}

export interface RoundBet {
  id: string
  user_data: RoundDetailUserData
  party_id: string
  party_type: "SOLO" | "TEAM"
  asset_class: "TON" | "TELEGRAM_GIFT"
  amount: number | null
  nft_id: string | null
  /** Fragment small-image URL for TELEGRAM_GIFT bets; absent for TON bets. */
  small_image_url?: string | null
  ton_equiv_nano: number
  status: "PENDING" | "ACTIVE" | "WON" | "LOST" | "CANCELED"
  created_at: number
  terminated_at: number | null
}

export interface RoundTrick {
  user_id: string
  type: "PUSH_WHEEL"
}

export interface PvpRouletteResolvedRound {
  id: string
  number: number
  room_name: string
  fairness_preimage?: string | null
  salt: string | null
  commit_hash: string | null
  client_commit_hash: string
  parties: ActualParty[]
  tricks: RoundTrick[]
  bets: RoundBet[]
  /** Backend may send int or decimal string; fairness verify normalizes. */
  roulette_position: number | string | null
  version: number
  created_at: number
}

export const getPvpRouletteRound = (roundId: string) =>
  apiClient.get<PvpRouletteResolvedRound>("/v1/games/pvp-roulette/round", {
    round_id: roundId,
  })
